import { useCallback, useEffect, useState } from "react";
import { UserPlus, UserMinus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Follow / unfollow toggle for a coach.
 *
 * Used on the coach directory card + the public coach profile. Hidden for
 * coaches viewing themselves and for admins acting as coaches (would be
 * confusing UX). Coach-role viewers just don't see the button — students only.
 *
 * Optimistic UI: flip immediately, roll back on error. Idempotent on the
 * backend (POST /follows twice is a no-op) so a double-tap can't wedge state.
 */
export default function FollowButton({ coachId, size = "md", onChange }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!coachId || !user) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/follows/status/${coachId}`);
      setFollowing(!!data.following);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [coachId, user]);

  useEffect(() => { load(); }, [load]);

  // Coach viewing themselves — hide.
  if (!user || user.id === coachId) return null;
  // Only students can follow.
  if (user.role !== "student") return null;

  const toggle = async () => {
    if (busy) return;
    const next = !following;
    setBusy(true);
    setFollowing(next); // optimistic
    try {
      if (next) await api.post("/follows", { coach_id: coachId });
      else await api.delete(`/follows/${coachId}`);
      if (onChange) onChange(next);
      toast(next ? "Following — you'll get updates in your feed." : "Unfollowed.", {
        duration: 2500,
      });
    } catch (e) {
      setFollowing(!next); // rollback
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update follow state");
    } finally {
      setBusy(false);
    }
  };

  const cls = size === "sm"
    ? "px-2.5 py-1 text-[10px]"
    : "px-3.5 py-2 text-xs";

  if (loading) {
    return (
      <div className={`${cls} inline-flex items-center gap-1.5 border border-[#262626] text-[#525252] uppercase tracking-wider`} data-testid="follow-button-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> …
      </div>
    );
  }

  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid={following ? "unfollow-button" : "follow-button"}
      className={`${cls} uppercase tracking-wider inline-flex items-center gap-1.5 transition-all disabled:opacity-50 border`}
      style={following
        ? { borderColor: "#262626", color: "#A3A3A3" }
        : { borderColor: "#CCFF00", background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 }}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : following ? <UserMinus className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
      {following ? "Following" : "Follow"}
    </button>
  );
}
